import { ButtonFormData, ListFormData } from "@/types/messages";
import { splitOutsideSpintax } from "@/utils/spintax";

function normalizePart(value: string | undefined): string {
  if (!value || value === "undefined") return "";
  return value;
}

export function parseMessageToForm(
  message: string
):
  | { type: "list"; list: ListFormData }
  | { type: "button"; button: ButtonFormData } {
  if (typeof message !== "string") {
    throw new Error("Invalid message input");
  }

  const trimmed = message.trim();
  if (trimmed.startsWith("#list|")) {
    // Format: #list|title|description|footer|buttonText|sectionStrings
    const parts = splitOutsideSpintax(trimmed, "|");
    if (parts.length < 6) {
      throw new Error("Malformed list message");
    }
    const [, title, description, footer, buttonText, sectionStringsRaw] = parts;
    if (!description) {
      throw new Error("List description is required");
    }

    const sectionEntries = sectionStringsRaw
      ? splitOutsideSpintax(sectionStringsRaw, "/")
      : [];
    // Each entry: SectionName*ItemTitle*ItemDescriptionOrUndefined*OptionId
    interface SectionAccumulator {
      [sectionName: string]: Array<{
        title: string;
        description?: string;
        optionId: string;
      }>;
    }
    const bySectionName: SectionAccumulator = {};

    for (const entry of sectionEntries) {
      if (!entry) continue;
      const [sectionName, itemTitle, itemDescription, optionId] =
        splitOutsideSpintax(entry, "*");
      if (!sectionName || !itemTitle || !optionId) {
        throw new Error("Invalid list item encoding");
      }
      const normalizedDescription = normalizePart(itemDescription);
      if (!bySectionName[sectionName]) bySectionName[sectionName] = [];
      bySectionName[sectionName].push({
        title: itemTitle,
        description: normalizedDescription || undefined,
        optionId,
      });
    }

    const sections: ListFormData["sections"] = Object.entries(
      bySectionName
    ).map(([name, items], idx) => ({
      id: `section-${idx + 1}`,
      name,
      items: items.map((it, jdx) => ({
        id: `item-${idx + 1}-${jdx + 1}`,
        title: it.title,
        description: it.description || "",
        optionId: it.optionId,
      })),
    }));

    if (sections.length === 0) {
      throw new Error("List must contain at least one section and item");
    }

    return {
      type: "list",
      list: {
        title: normalizePart(title),
        description,
        footer: normalizePart(footer),
        buttonText: normalizePart(buttonText),
        sections,
      },
    };
  }

  if (trimmed.startsWith("#btn|")) {
    // Format: #btn|title|subTitleOrUndefined|mediaPart|buttonParts
    const parts = splitOutsideSpintax(trimmed, "|");
    if (parts.length < 5) {
      throw new Error("Malformed button message");
    }
    const [, title, subTitleRaw, mediaRaw, ...buttonRest] = parts;
    if (!title) {
      throw new Error("Button title is required");
    }

    const subTitle = normalizePart(subTitleRaw);

    let mediaType: ButtonFormData["mediaType"] = "none";
    let mediaUrl = "";
    let mediaFilename = "";
    if (mediaRaw && mediaRaw !== "undefined") {
      const mediaParts = splitOutsideSpintax(mediaRaw, "*");
      const mType = mediaParts[0];
      const mUrl = mediaParts[1];
      const mFilename = mediaParts[2];

      if (mType !== "image" && mType !== "video" && mType !== "document") {
        throw new Error("Invalid media type");
      }
      mediaType = mType;
      mediaUrl = mUrl || "";
      mediaFilename = mFilename || "";
      if (!mediaUrl) {
        throw new Error("Media URL missing");
      }
    }

    const buttonJoined = buttonRest.join("|");
    const buttonParts = buttonJoined
      ? splitOutsideSpintax(buttonJoined, "|")
      : [];
    const buttons: ButtonFormData["buttons"] = buttonParts.map((bp) => {
      const segs = splitOutsideSpintax(bp, "*");
      const type = segs[0] as ButtonFormData["buttons"][number]["type"];
      const text = segs[1];
      const third = segs[2];
      if (!type || !text) {
        throw new Error("Invalid button encoding");
      }
      if (
        type !== "cta_copy" &&
        type !== "cta_call" &&
        type !== "cta_url" &&
        type !== "quick_reply" &&
        type !== "voice_call"
      ) {
        throw new Error("Unknown button type");
      }
      if (type === "cta_url") {
        if (!third) throw new Error("URL button missing URL");
        return { type, text, url: third };
      }
      if (type === "cta_call") {
        if (!third) throw new Error("Call button missing phone number");
        return { type, text, phoneNumber: third };
      }
      if (type === "cta_copy") {
        if (!third) throw new Error("Copy button missing code");
        return { type, text, copyCode: third };
      }
      if (type === "voice_call") {
        return { type, text };
      }
      // quick_reply
      if (!third) throw new Error("Quick reply missing id");
      return { type, text, id: third };
    });

    if (buttons.length === 0) {
      throw new Error("At least one button is required");
    }

    return {
      type: "button",
      button: {
        title,
        subTitle,
        mediaType,
        mediaUrl: mediaType === "none" ? "" : mediaUrl,
        mediaFilename: mediaType === "none" ? "" : mediaFilename,
        buttons,
      },
    };
  }

  throw new Error("Unrecognized message format");
}
